import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Undo from '../../svg/Undo'
import Ban from '../../svg/Ban'
import AlertCircle from '../../svg/AlertCirle'
import InvalidElement from '../../../../domain/Folder/InvalidElementInterface'
import { EFolderStatus } from '../../../../domain/Folder/EFolderStatus'

interface ElementProps {
  element: any
  blockId: string
  state: string | null
  pageId: string
  invalidElement: InvalidElement | null
  onInvalidateToggle: (
    isInvalid: boolean,
    elementId: string,
    comment: string,
    blockId: string,
    receiptId: string | null,
    value: string | null,
    pageId: string,
  ) => void
  onCommentChange: (elementId: string, receiptId: string|null, newComment: string) => void
}

const PresentationElement = ({ element, blockId, state, pageId, invalidElement, onInvalidateToggle, onCommentChange }: ElementProps): JSX.Element => {
  const { t } = useTranslation()

  const [isInvalid, setIsInvalid] = useState(false)
  const [comment, setComment] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
      if (invalidElement) {
        setIsInvalid(true)
        setComment(invalidElement.comment)
      }
    }, [invalidElement])

  const toggleInvalid = () => {
    const newInvalidState = !isInvalid
    setIsInvalid(newInvalidState)

    const initialComment = newInvalidState ? comment : ""

    onInvalidateToggle(newInvalidState, element.id, initialComment, blockId, null, element.value, pageId);
    if (!newInvalidState) {
      setComment('')
    } else if (inputRef.current) {
      setTimeout(() => inputRef.current?.focus())
    }
  };

  const handleInputCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newComment = e.target.value;
    setComment(newComment);

    if (isInvalid) {
      onCommentChange(element.id, null, newComment);
    }
  };

  const renderElementValue = (value: string | number | [] | boolean) => {
    if (Array.isArray(value)) {
      return (
        <ul className="list-reset">
          {value.map((item) => (
            <li key={item} className="u-mxs u-txt-size-s">- {item}</li>
          ))}
        </ul>
      )
    }

    if (typeof value === 'boolean') {
      return (
        <span className="u-txt-size-s">{value ? t('common.yes') : t('common.no')}</span>
      )
    }

    return <span className="u-txt-size-s">{value}</span>;
  };

  const rowClasses = `custom-doc-row ${isInvalid ? 'u-pas u-mas invalid' : 'u-pxs u-mxs margin-10'}`;
  const buttonBaseClasses = 'flex items-center gap-1 u-pxs u-pys rounded text-xs font-medium transition-all';
  const buttonDynamicClasses = isInvalid
    ? 'btn-active-state'
    : 'btn-default-state';

  const commentSectionClasses = `comment-section ml-9 ${isInvalid ? 'open' : ''}`;

  return (
    <div key={element.id} className={rowClasses}>
      <div className='row'>
        <div className='col-md-10 u-mb0 content-center'>
          <span className="u-txt-bold u-txt-size-s">{element.presentation}</span>{element.presentation.endsWith('?') ? '' : ' :'} {renderElementValue(element.value)}
        </div>
        <div className='col-md-2 u-mb0 content-center'>
          {("ongoing" === state || "error" === state || EFolderStatus.Pre_signature_validation === state) &&
            <a id={`btn-${element.id}`} className={`button ${buttonBaseClasses} ${buttonDynamicClasses}`}
              onClick={toggleInvalid}
            >
              {isInvalid ? (
                <>
                  <Undo color='var(--error)' />
                  <span>{t('button.restore')}</span>
                </>
              ) : (
                <>
                  <Ban color='#94a3b8' />
                  <span>{t('button.invalid')}</span>
                </>
              )}
            </a>
          }
        </div>
      </div>

      <div id={`comment-box-${element.id}`} className={commentSectionClasses}>
        <div className="relative mt-2">
          <div className="u-absolute inset-y-0 left-0 flex items-center u-pxs pointer-events-none">
            <AlertCircle />
          </div>
          <input
            type="text"
            id={`input-${element.id}`}
            placeholder={t('validation.placeholder-error-input')}
            className="block w-full pl-9 pr-3 py-2 sm:text-sm custom-input-error bg-white text-slate-800 placeholder-slate-400 border-red-200"
            value={comment}
            onChange={handleInputCommentChange}
            disabled={!isInvalid}
            ref={inputRef}
          />
        </div>
      </div>
    </div>
  );
};

export default PresentationElement;
